import { Txt } from 'components/Txt';
import { theme } from 'lib/theme';
import React from 'react';
import {
  Platform,
  StyleProp,
  StyleSheet,
  View,
  ViewProps,
  ViewStyle,
} from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withTiming,
  withDelay,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Button, ButtonTheme } from './Button';
import { Icon, ICONS } from './Icon';
import { IconButton, IconButtonProps } from './IconButton';
import { Menu, MenuProps } from './Menu';

export const NAVIGATION_HEADER_HEIGHT = 56;

export type NavigationHeaderProps = {
  onPressBackButton?: () => void;
  onPressCloseButton?: () => void;
  onPressCancelButton?: () => void;
  onPressSaveButton?: (() => void) | null;
  onPressSubheading?: () => void;
  heading?: string;
  headingNumberOfLines?: number;
  subheading?: string;
  subheadingIcon?: ICONS;
  buttons?: React.ReactNode;
  buttonsLeft?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  headerStyle?: StyleProp<ViewStyle>;
  iconButtonLeft?: IconButtonProps;
  iconButtonRight?: IconButtonProps;
  menuRight?: MenuProps | false;
  saveButtonText?: string;
  saveButtonTheme?: ButtonTheme;
  closeIcon?: ICONS;
  buttonsLeftStyle?: StyleProp<ViewStyle>;
  cancelButtonText?: string;
  children?: React.ReactNode;
  marginLeft?: number;
  marginRight?: number;
  onLayout?: ViewProps['onLayout'];
};

const CloseButton: React.FC<{
  text: string;
  onPress: () => void;
}> = ({ text, onPress }) => {
  const opacityRef = useSharedValue(1);

  React.useEffect(() => {
    opacityRef.value = withSequence(
      withDelay(500, withTiming(0, { duration: 250, easing: Easing.ease })),
      withDelay(
        1000,
        withTiming(1, {
          duration: 250,
          easing: Easing.ease,
        }),
      ),
    );
  }, [opacityRef]);

  const animatedStyles = useAnimatedStyle(() => {
    return {
      opacity: opacityRef.value,
    };
  });

  const altAnimatedStyles = useAnimatedStyle(() => {
    return {
      opacity: 1 - opacityRef.value,
    };
  });

  return (
    <View style={styles.buttonCancel}>
      <View
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Button
          text={' '}
          buttonTheme={ButtonTheme.themeless}
          textSize="large"
          textWeight="regular"
          onPress={onPress}
          uppercase={false}
          style={{ width: '100%' }}
          textStyle={{ textAlign: 'center', width: '100%' }}
          inHeader
        />
      </View>
      <Animated.View
        style={[
          {
            position: 'absolute',
            left: 0,
            top: 0,
            right: 0,
            bottom: 0,
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1,
            pointerEvents: 'none',
          },
          altAnimatedStyles,
        ]}
      >
        <Button
          text="Esc"
          buttonTheme={ButtonTheme.themeless}
          textSize="large"
          textWeight="regular"
          onPress={onPress}
          uppercase={false}
          style={{ width: '100%' }}
          textStyle={{ textAlign: 'center', width: '100%' }}
          inHeader
          focusable={false}
        />
      </Animated.View>
      <Animated.View style={[animatedStyles, { pointerEvents: 'none' }]}>
        <Button
          text={text}
          buttonTheme={ButtonTheme.themeless}
          textSize="large"
          textWeight="regular"
          onPress={onPress}
          uppercase={false}
          inHeader
          focusable={false}
        />
      </Animated.View>
    </View>
  );
};

export const NavigationHeader: React.FC<NavigationHeaderProps> = ({
  buttons,
  buttonsLeft,
  buttonsLeftStyle,
  cancelButtonText = 'Cancel',
  children,
  closeIcon,
  style,
  contentStyle,
  headerStyle,
  heading,
  headingNumberOfLines = 1,
  iconButtonLeft,
  iconButtonRight,
  menuRight,
  onPressBackButton,
  onPressCancelButton,
  onPressCloseButton,
  onPressSaveButton,
  saveButtonText = 'Save',
  saveButtonTheme = ButtonTheme.themeless,
  subheading,
  subheadingIcon,
  onPressSubheading,
  marginLeft,
  marginRight,
  onLayout,
}) => {
  /**
   * Use useSafeAreaInsets instead of SafeAreaView component
   * otherwise there is vertical jumping when a page is opened
   */
  const safeAreaInsets = useSafeAreaInsets();

  const hasHeaderButtons = !!onPressCancelButton || !!onPressSaveButton;
  const hasHeaderIconButtons =
    !!onPressCloseButton ||
    !!onPressBackButton ||
    !!iconButtonLeft ||
    !!iconButtonRight;
  const contentHorizontalMarginLeft =
    marginLeft !== undefined
      ? marginLeft
      : hasHeaderButtons
        ? 70
        : hasHeaderIconButtons
          ? 36 + theme.paddingHorizontal
          : theme.paddingHorizontal;
  const contentHorizontalMarginRight =
    marginRight !== undefined
      ? marginRight
      : hasHeaderButtons
        ? 70
        : hasHeaderIconButtons
          ? 36 + theme.paddingHorizontal
          : theme.paddingHorizontal;

  return (
    <View
      style={[
        {
          marginLeft: safeAreaInsets.left,
          marginRight: safeAreaInsets.right,
        },
        styles.view,
        style,
      ]}
      onLayout={onLayout}
    >
      {onPressCancelButton &&
        (Platform.OS === 'web' ? (
          <CloseButton text={cancelButtonText} onPress={onPressCancelButton} />
        ) : (
          <Button
            text={cancelButtonText}
            buttonTheme={ButtonTheme.themeless}
            textSize="large"
            textWeight="regular"
            onPress={onPressCancelButton}
            uppercase={false}
            style={styles.buttonCancel}
            inHeader
          />
        ))}
      {onPressCloseButton && (
        <IconButton
          style={styles.buttonLeft}
          icon={closeIcon || ICONS.XmarkLight}
          onPress={onPressCloseButton}
        />
      )}
      {onPressBackButton && (
        <IconButton
          style={styles.buttonLeft}
          imageStyle={{ transform: [{ rotate: '-180deg' }], marginLeft: -3 }}
          icon={ICONS.ChevronRightLight}
          onPress={onPressBackButton}
        />
      )}
      {!!buttonsLeft && (
        <View style={[styles.buttonsLeft, buttonsLeftStyle]}>
          {buttonsLeft}
        </View>
      )}
      {iconButtonLeft && (
        <View style={styles.iconButtonLeft}>
          <IconButton
            active={iconButtonLeft.active}
            iconSize={iconButtonLeft.iconSize}
            color={iconButtonLeft.color}
            disabled={iconButtonLeft.disabled}
            imageStyle={iconButtonLeft.imageStyle}
            icon={iconButtonLeft.icon}
            marginHorizontal={iconButtonLeft.marginHorizontal}
            onPress={iconButtonLeft.onPress}
            onPressIn={iconButtonLeft.onPressIn}
            size={iconButtonLeft.size}
            style={iconButtonLeft.style}
            showBadge={iconButtonLeft.showBadge}
          />
        </View>
      )}
      {iconButtonRight && (
        <View style={styles.iconButtonRight}>
          <IconButton
            active={iconButtonRight.active}
            iconSize={iconButtonRight.iconSize}
            color={iconButtonRight.color}
            disabled={iconButtonRight.disabled}
            imageStyle={iconButtonRight.imageStyle}
            icon={iconButtonRight.icon}
            marginHorizontal={iconButtonRight.marginHorizontal}
            onPress={iconButtonRight.onPress}
            onPressIn={iconButtonRight.onPressIn}
            size={iconButtonRight.size}
            style={iconButtonRight.style}
            showBadge={iconButtonRight.showBadge}
          />
        </View>
      )}
      {menuRight && (
        <View
          style={[
            styles.iconButtonRight,
            onPressSaveButton !== undefined ? { marginRight: 54 } : {},
          ]}
        >
          <Menu {...menuRight} />
        </View>
      )}
      {onPressSaveButton !== undefined && (
        <Button
          text={saveButtonText}
          textSize="large"
          buttonTheme={saveButtonTheme}
          onPress={onPressSaveButton || undefined}
          textWeight="bold"
          uppercase={false}
          style={styles.buttonSave}
          inHeader
        />
      )}
      <View
        style={[
          styles.content,
          {
            marginLeft: contentHorizontalMarginLeft,
            marginRight: contentHorizontalMarginRight,
          },
          contentStyle,
        ]}
      >
        {!!heading && (
          <View style={[styles.heading, headerStyle]}>
            <Txt
              size="large"
              weight="bold"
              numberOfLines={headingNumberOfLines}
            >
              {heading}
            </Txt>
            {!!subheading &&
              (onPressSubheading ? (
                <Button
                  icon={subheadingIcon}
                  iconSize={15}
                  buttonTheme={ButtonTheme.link}
                  onPress={onPressSubheading}
                  text={subheading}
                  textSize="small"
                  textWeight="regular"
                  style={{ marginTop: -5 }}
                />
              ) : (
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  {!!subheadingIcon && (
                    <Icon
                      source={subheadingIcon}
                      size={15}
                      color={theme.colors.fieldValueText}
                      style={{
                        marginTop: -5,
                        marginRight: theme.paddingHorizontal / 2,
                      }}
                    />
                  )}
                  <Txt
                    size="small"
                    numberOfLines={1}
                    style={{
                      marginTop: -5,
                      color: theme.colors.fieldValueText,
                    }}
                  >
                    {subheading}
                  </Txt>
                </View>
              ))}
          </View>
        )}
        {children}
      </View>
      <View style={styles.buttonsRight}>{buttons}</View>
    </View>
  );
};

const styles = StyleSheet.create({
  buttonCancel: {
    left: theme.paddingHorizontal,
    position: 'absolute',
    zIndex: 1,
  },
  buttonLeft: {
    left: theme.paddingHorizontal,
    position: 'absolute',
    zIndex: 1,
  },
  buttonSave: {
    right: theme.paddingHorizontal,
    position: 'absolute',
    zIndex: 1,
  },
  buttonsLeft: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    left: theme.paddingHorizontal,
    position: 'absolute',
    zIndex: 1,
  },
  buttonsRight: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    position: 'absolute',
    right: theme.paddingHorizontal,
    zIndex: 1,
  },
  content: {
    flex: 1,
    alignItems: 'center',
    flexDirection: 'row',
    paddingHorizontal: theme.paddingHorizontal,
  },
  heading: {
    flex: 1,
    marginTop: 1,
    alignItems: 'center',
  },
  iconButtonLeft: {
    position: 'absolute',
    left: theme.paddingHorizontal,
    zIndex: 1,
  },
  iconButtonRight: {
    position: 'absolute',
    right: theme.paddingHorizontal,
    zIndex: 1,
  },
  view: {
    justifyContent: 'center',
    height: NAVIGATION_HEADER_HEIGHT,
  },
});
